import { useEffect } from "react";
import { useRouter } from "next/router";
import { fetchCategoriesForBranch } from "../../utils/APICalls/APICalls";
import LS from "../../utils/LocalStorage";
import { LocalStorageEnum, PagesEnum } from "../../utils/enums";
import envConfig from "../../utils/env.config";
import { setEntityImageLookup } from "../redux/slices/configSlice";
import { useAppDispatch, useAppSelector } from "./useReduxHooks";

/**
 * Keeps `config.entityImageLookup` populated on every route, not just /home.
 *
 * The lookup maps a menu entity to its image and item id, and loyalty offers are
 * judged against it: `LoyaltyItemOffer.hasDeletedItems` treats an entity that is
 * absent from the lookup as a deleted menu item, and the card falls back to the
 * gift icon when it cannot resolve an image. Its initial value is `{}` - truthy,
 * so it skips that method's `!entityImageLookup` escape hatch - which means an
 * unpopulated lookup makes every item-based offer render as
 * "Item(s) unavailable" with no picture.
 *
 * Until now the only dispatch was in pages/home.tsx, as part of that page's menu
 * flow. Any route reached without passing through /home therefore judged offers
 * against an empty menu: offline mode (a guest arriving on /r/<shortId> is
 * redirected straight to /offline-mode), the loyalty card and the wallet screens.
 *
 * /home is left to its own flow, which has richer sources (SSG props, per-branch
 * custom prices) and runs synchronously on mount.
 */
export const useEntityImageLookup = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const selectedFranchise = useAppSelector(
    (state) => state.config.selectedFranchise
  );
  const hasLookup = useAppSelector(
    (state) => Object.keys(state.config.entityImageLookup ?? {}).length > 0
  );

  const restaurantId =
    restaurantInfo?.id || parseInt(envConfig.restaurantId ?? "", 10);
  const franchiseSlug = selectedFranchise?.slug;
  const isHomePage = router.pathname.includes(PagesEnum.Home);

  useEffect(() => {
    if (hasLookup || isHomePage || !Number.isFinite(restaurantId)) {
      return;
    }

    // The cached menu is free and usually present; only pay for a request when
    // it is not. Version-independent on purpose: a stale image or item id still
    // beats judging every offer against an empty menu, and /home replaces the
    // lookup with a version-checked build as soon as the guest goes there.
    const menuLS = LS._get(LocalStorageEnum.MENU_DATA, true);
    if (menuLS?.categoriesData?.length) {
      dispatch(setEntityImageLookup(menuLS.categoriesData));

      return;
    }

    let cancelled = false;
    fetchCategoriesForBranch(restaurantId, franchiseSlug)
      .then((categories) => {
        if (!cancelled && categories?.length) {
          dispatch(setEntityImageLookup(categories));
        }
      })
      // An offer judged available with no picture is a better failure than every
      // offer falsely blocked, and that is what an empty lookup already gives.
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [dispatch, hasLookup, isHomePage, restaurantId, franchiseSlug]);
};
